import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./EventPage.css";
import logo from "../../assets/logo.webp";
import { assetUrl } from "../../utils/useContent";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5001";
const GRID_COLS = 12;
const ROW_HEIGHT = 60; // px per row

export default function EventPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/events/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) {
          if (!cancelled) setNotFound(true);
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((data) => !cancelled && setPage(data))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  // Sort widgets by row-then-column so mobile stacking order makes sense, and
  // record how many empty grid rows sat above each one on the desktop canvas.
  // Collapsing to a single column throws that whitespace away, which is what
  // makes a stacked page feel crammed — the CSS turns `gapRows` back into
  // margin so the author's spacing survives on phones.
  const widgets = useMemo(() => {
    const list = [...(page?.widgets || [])].sort((a, b) => {
      const ay = a.layout?.y ?? 0;
      const by = b.layout?.y ?? 0;
      if (ay !== by) return ay - by;
      return (a.layout?.x ?? 0) - (b.layout?.x ?? 0);
    });

    let bottom = 0; // lowest row occupied by any widget seen so far
    return list.map((w) => {
      const y = w.layout?.y ?? 0;
      const h = w.layout?.h ?? 2;
      // Widgets sitting beside one another share a row, so their gap resolves
      // to 0 and only the first of the pair pushes the stack down.
      const gapRows = Math.max(0, y - bottom);
      bottom = Math.max(bottom, y + h);
      return { ...w, gapRows, rows: h };
    });
  }, [page]);

  if (loading) {
    return (
      <main className="ep-root">
        <div className="container ep-container"><div className="ep-loading">Loading…</div></div>
      </main>
    );
  }

  if (notFound || !page) {
    return (
      <>
        <SEO title="Page not found | ASX" description="This event page doesn't exist or has been removed." noindex />
        <main className="ep-root ep-root-centered">
          <div className="container ep-container">
            <div className="ep-state-card">
              <div className="ep-state-title">Page not found</div>
              <div className="ep-state-sub">This event page doesn't exist or has been removed.</div>
              <Link to="/" className="ep-btn ep-btn-primary">Back to All Star Xperience</Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const accentStyle = page.accent_color
    ? { "--ep-accent": page.accent_color }
    : undefined;

  return (
    <>
      <SEO
        title={`${page.title || "Event"} | All Star Xperience Montreal`}
        description={page.description || `${page.title || "ASX event"} — details, schedule, and info from All Star Xperience Montreal.`}
        image={page.banner_image_path ? assetUrl(page.banner_image_path) : undefined}
      />
      <main className="ep-root" style={accentStyle}>
        {page.banner_image_path && (
          <div className="ep-banner">
            <img src={assetUrl(page.banner_image_path)} alt="" />
          </div>
        )}

        <div className="container ep-container">
          <div
            className="ep-grid"
            style={{ gridAutoRows: `${ROW_HEIGHT}px` }}
          >
            {widgets.map((w) => (
              <div
                key={w.id}
                className="ep-widget"
                data-type={w.type}
                style={{
                  gridColumnStart: (w.layout?.x ?? 0) + 1,
                  gridColumnEnd: (w.layout?.x ?? 0) + (w.layout?.w ?? GRID_COLS) + 1,
                  gridRowStart: (w.layout?.y ?? 0) + 1,
                  gridRowEnd: (w.layout?.y ?? 0) + (w.layout?.h ?? 2) + 1,
                  "--ep-gap-rows": w.gapRows,
                  "--ep-rows": w.rows,
                }}
              >
                <WidgetRenderer widget={w} />
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

/* ============================================================
   Widget renderers
   ============================================================ */
function textStyle(c) {
  const style = {};
  if (c.color) style.color = c.color;
  // The size chosen in the admin is handed to CSS as a custom property rather
  // than a hard font-size, so narrow screens can scale it down instead of
  // rendering a 60px headline on a 390px phone.
  if (c.size) style["--ep-fs"] = `${c.size}px`;
  return style;
}

function WidgetRenderer({ widget }) {
  const c = widget.config || {};
  switch (widget.type) {
    case "title":
      return (
        <h1 className={`ep-w-title ep-align-${c.align || "left"}`} style={textStyle(c)}>
          {c.text || ""}
        </h1>
      );
    case "subtitle":
      return (
        <h2 className={`ep-w-subtitle ep-align-${c.align || "left"}`} style={textStyle(c)}>
          {c.text || ""}
        </h2>
      );
    case "text":
      return (
        <p className={`ep-w-text ep-align-${c.align || "left"}`} style={textStyle(c)}>
          {c.text || ""}
        </p>
      );
    case "image":
      return c.path ? (
        <div className="ep-w-image-wrap">
          <img src={assetUrl(c.path)} alt="" className="ep-w-image" />
        </div>
      ) : <div className="ep-w-placeholder">No image</div>;
    case "video":
      const embed = toYouTubeEmbed(c.url);
      return embed ? (
        <div className="ep-w-video-wrap">
          <iframe
            className="ep-w-video"
            src={embed}
            title="Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : <div className="ep-w-placeholder">Invalid video link</div>;
    case "button":
      if (!c.label || !c.url) return <div className="ep-w-placeholder">Button</div>;
      const isExternal = /^https?:\/\//i.test(c.url);
      const size = c.size || "md";
      return (
        <div className={`ep-w-button-wrap ep-align-${c.align || "center"}`}>
          <a
            href={c.url}
            className={`ep-btn ep-btn-${size} ${c.variant === "ghost" ? "ep-btn-ghost" : "ep-btn-primary"}`}
            {...(isExternal ? { target: "_blank", rel: "noreferrer" } : {})}
          >
            {c.label}
          </a>
        </div>
      );
    case "divider":
      return <hr className="ep-w-divider" />;
    case "subscribe": {
      const ssize = c.size || "md";
      const scrollToSubscribe = (e) => {
        e.preventDefault();
        const target = document.getElementById("subscribe");
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          // Nudge focus to the input for keyboard users after scroll settles.
          setTimeout(() => target.querySelector("input[type=email]")?.focus(), 500);
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        }
      };
      return (
        <div className={`ep-w-button-wrap ep-align-${c.align || "center"}`}>
          <a
            href="#subscribe"
            onClick={scrollToSubscribe}
            className={`ep-btn ep-btn-${ssize} ${c.variant === "ghost" ? "ep-btn-ghost" : "ep-btn-primary"}`}
          >
            {c.label || "Subscribe to newsletter"}
          </a>
        </div>
      );
    }
    default:
      return null;
  }
}

function toYouTubeEmbed(rawUrl) {
  if (!rawUrl) return null;
  const url = String(rawUrl).trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

/* ---------- Footer (matches the rest of the site) ---------- */
function Footer() {
  const year = new Date().getFullYear();

  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              width="240"
              height="240"
              className="ft-logo-img"
              style={{ display: "block", objectFit: "contain" }}
            />
          </div>

          <div className="ft-social">
            <a className="ft-social-btn" href="https://www.instagram.com/allstarxperience/#"
               target="_blank" rel="noreferrer" aria-label="Instagram" title="Instagram">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"/>
              </svg>
              <span>Instagram</span>
            </a>
            <a className="ft-social-btn" href="https://www.facebook.com/allstarxperience"
               target="_blank" rel="noreferrer" aria-label="Facebook" title="Facebook">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"/>
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li><span className="ft-k">Email</span><a href="mailto:allstarxperience@gmail.com">allstarxperience@gmail.com</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14387283726">+1 (438) 728-3726</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14385089880">+1 (438) 508-9880</a></li>
            <li><span className="ft-k">City</span><span>Montreal, QC</span></li>
          </ul>
        </div>

        <div className="ft-col explore-col">
          <h4 className="ft-title explore-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>

        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>
          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachiッ.</span>
          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
